import { Edge, TestResults } from '../../../types';
import { capitaliseFirstLetter, edgeNameMap, round } from '../../../utils';

export interface NetworkTestResultDetail {
  label: string;
  value: string;
}

const NOT_AVAILABLE = 'N/A';

const formatEdge = (edge?: string): string => {
  if (!edge) {
    return NOT_AVAILABLE;
  }
  return edgeNameMap[edge as Edge] ?? capitaliseFirstLetter(edge);
};

const formatDuration = (durationMs?: number): string =>
  typeof durationMs === 'number' ? `${round(durationMs / 1000)}s` : NOT_AVAILABLE;

const formatMilliseconds = (value?: number): string =>
  typeof value === 'number' ? `${round(value)} ms` : NOT_AVAILABLE;

const formatPercentage = (fraction?: number): string =>
  typeof fraction === 'number' ? `${round(fraction * 100)}%` : NOT_AVAILABLE;

/**
 * The single source of truth for "what does a completed network test result look like".
 * Used by the Network Test widget's per-edge tooltip, the Results pane, and the PDF/JSON
 * export, so all three read the same fields the same way instead of re-deriving them
 * independently and silently drifting apart.
 */
export function getNetworkTestResultDetails(result: TestResults): NetworkTestResultDetail[] {
  const report = result.results.preflight;

  return [
    { label: 'Edge location', value: formatEdge(report?.edge ?? result.edge) },
    { label: 'Time to connect', value: formatDuration(report?.networkTiming?.peerConnection?.duration) },
    {
      label: 'Expected quality',
      value: report?.callQuality ? capitaliseFirstLetter(report.callQuality) : NOT_AVAILABLE,
    },
    { label: 'Call SID', value: report?.callSid ?? NOT_AVAILABLE },
    { label: 'Jitter', value: formatMilliseconds(report?.stats?.jitter.average) },
    { label: 'Latency', value: formatMilliseconds(report?.stats?.rtt.average) },
    { label: 'Packet loss', value: formatPercentage(report?.totals?.packetsLostFraction) },
  ];
}
